/**
 * Argus — browserRecorder (Bloc recording-browser-pivot 2026-05-09).
 *
 * Singleton wrapper peste Native MediaRecorder API. Tap pe MediaStream-ul live (extras
 * via `videoElement.captureStream()`) → accumulate Blob chunks în memorie → la stop()
 * concat în single .webm + auto-download via `<a download>` click. ZERO server impact.
 *
 * Pattern: `Map<recordingKey, Entry>` cheie `${agentIdHex}|${source}` (un recording per
 * agent+source — operator nu poate face 2 recordings simultan pe aceeași sursă pe același agent).
 *
 * Filename: `<timestamp> [<PC_Name>][<Win_User>][<source>].webm`
 *  - timestamp UTC: `YYYY-MM-DD HH.mm.ss` (DOTS în loc de `:` → Windows-compat native)
 *  - exemplu: `2026-05-09 14.30.00 [SYNCRO][iulic][desktop].webm`
 *
 * Refresh browser mid-record = recording lost (per user decision). Memory pressure long
 * recording: documentat ca limitare v1, defer IndexedDB-incremental la pain point real.
 */

export type RecordingSource = 'desktop' | 'webcam' | 'audio';

export interface BrowserRecorderEntry {
  recordingKey: string;
  agentId: string;
  pcName: string;
  windowsUsername: string;
  source: RecordingSource;
  startedAt: Date;
  segmentCount: number;
  totalBytes: number;
  state: 'recording' | 'stopping';
}

interface InternalEntry extends BrowserRecorderEntry {
  recorder: MediaRecorder;
  chunks: Blob[];
  mimeType: string;
}

type Listener = (entries: ReadonlyArray<BrowserRecorderEntry>) => void;

function pickMimeType(source: RecordingSource): string {
  const candidates = source === 'audio'
    ? ['audio/webm;codecs=opus', 'audio/webm']
    : ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  throw new Error('Browser nu suportă MediaRecorder cu WebM (Safari < 14 sau browser foarte vechi).');
}

function buildTimestamp(d: Date): string {
  // YYYY-MM-DD HH.mm.ss UTC. Dots în loc de `:` → filename Windows-compat native.
  const iso = d.toISOString(); // "2026-05-09T14:30:00.123Z"
  return `${iso.slice(0, 10)} ${iso.slice(11, 19).replace(/:/g, '.')}`;
}

function sanitizeFilenameSegment(s: string): string {
  // Remove caractere care confuzează filesystems (Windows + macOS + Linux): / \ : * ? " < > |
  // Plus newlines/tabs. Spaces le păstrăm — sunt OK în filename modern.
  return s.replace(/[\/\\:*?"<>|\r\n\t]/g, '_').trim() || 'unknown';
}

function buildFilename(startedAt: Date, pcName: string, winUser: string, source: RecordingSource): string {
  const ts = buildTimestamp(startedAt);
  const pc = sanitizeFilenameSegment(pcName);
  const user = sanitizeFilenameSegment(winUser);
  return `${ts} [${pc}][${user}][${source}].webm`;
}

class BrowserRecorder {
  private readonly entries = new Map<string, InternalEntry>();
  private readonly listeners = new Set<Listener>();

  /**
   * Start recording. Throws if MediaRecorder unsupported sau dacă (agentId, source) e deja active.
   */
  start(opts: {
    agentId: string;
    pcName: string;
    windowsUsername: string;
    source: RecordingSource;
    mediaStream: MediaStream;
    bitrate?: number;
  }): string {
    const recordingKey = `${opts.agentId.toUpperCase()}|${opts.source}`;
    if (this.entries.has(recordingKey)) {
      throw new Error(`Recording already active pentru (${opts.agentId}, ${opts.source}).`);
    }
    const mimeType = pickMimeType(opts.source);
    const recorderOpts: MediaRecorderOptions = { mimeType };
    if (opts.source !== 'audio' && opts.bitrate) recorderOpts.videoBitsPerSecond = opts.bitrate;
    if (opts.source === 'audio' && opts.bitrate) recorderOpts.audioBitsPerSecond = opts.bitrate;

    const recorder = new MediaRecorder(opts.mediaStream, recorderOpts);
    const chunks: Blob[] = [];
    const startedAt = new Date();

    const entry: InternalEntry = {
      recordingKey,
      agentId: opts.agentId.toUpperCase(),
      pcName: opts.pcName,
      windowsUsername: opts.windowsUsername,
      source: opts.source,
      startedAt,
      segmentCount: 0,
      totalBytes: 0,
      state: 'recording',
      recorder,
      chunks,
      mimeType,
    };

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunks.push(event.data);
        entry.segmentCount += 1;
        entry.totalBytes += event.data.size;
        this.notify();
      }
    };

    recorder.onstop = () => {
      try {
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const filename = buildFilename(entry.startedAt, entry.pcName, entry.windowsUsername, entry.source);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1500);
        // eslint-disable-next-line no-console
        console.info(`browserRecorder: download triggered ${filename} (${blob.size} bytes, ${chunks.length} chunks)`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('browserRecorder: download trigger failed', err);
      }
      this.entries.delete(recordingKey);
      this.notify();
    };

    recorder.onerror = (ev) => {
      // eslint-disable-next-line no-console
      console.error('browserRecorder: MediaRecorder error', ev);
    };

    // Auto-stop dacă MediaStream devine inactive (stream ended → tracks ended).
    const onStreamInactive = () => {
      if (this.entries.has(recordingKey) && recorder.state !== 'inactive') {
        try { recorder.stop(); } catch { /* already stopped */ }
      }
    };
    opts.mediaStream.addEventListener('inactive', onStreamInactive);

    // Timeslice 1s — emite ondataavailable la fiecare secundă (chunk Blob).
    recorder.start(1000);
    this.entries.set(recordingKey, entry);
    this.notify();
    return recordingKey;
  }

  stop(recordingKey: string): void {
    const entry = this.entries.get(recordingKey);
    if (!entry) return;
    if (entry.recorder.state === 'inactive') {
      this.entries.delete(recordingKey);
      this.notify();
      return;
    }
    entry.state = 'stopping';
    this.notify();
    try { entry.recorder.stop(); } // declanșează onstop → cleanup + download
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn('browserRecorder.stop() failed', err);
      this.entries.delete(recordingKey);
      this.notify();
    }
  }

  /** Stop recording specific la (agentId, source). Wrapper pentru pattern caller. */
  stopForAgent(agentId: string, source: RecordingSource): void {
    this.stop(`${agentId.toUpperCase()}|${source}`);
  }

  isActive(agentId: string, source: RecordingSource): boolean {
    return this.entries.has(`${agentId.toUpperCase()}|${source}`);
  }

  getActive(): ReadonlyArray<BrowserRecorderEntry> {
    return Array.from(this.entries.values()).map((e) => ({
      recordingKey: e.recordingKey,
      agentId: e.agentId,
      pcName: e.pcName,
      windowsUsername: e.windowsUsername,
      source: e.source,
      startedAt: e.startedAt,
      segmentCount: e.segmentCount,
      totalBytes: e.totalBytes,
      state: e.state,
    }));
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private notify(): void {
    const snapshot = this.getActive();
    for (const l of this.listeners) {
      try { l(snapshot); } catch { /* listener failure must not break others */ }
    }
  }
}

export const browserRecorder = new BrowserRecorder();
